import React from "react";
import PropTypes from "prop-types";

import { Sparkle } from "../controls";

/**
 * Step 4 — the small "Review captured items" dialog that pops right after
 * the care manager clicks Review call. Shows a per-section count of what
 * the AI captured during the call, plus a total. `Start review` advances
 * to step 5 (the full Review & save layout).
 *
 * The dialog is deliberately preview-only. No accept/discard here — that
 * happens on the next screen where each entry is visible in context.
 */

// Section id (Go slug) → human-facing label used on the accordion row.
const SECTION_LABEL = {
  patient_details:   "Patient details",
  chief_complaints:  "Chief complaints",
  diagnosis:         "Diagnosis",
  medicines:         "Medicines",
  hospitalisation:   "Hospitalisation history",
  allergies:         "Allergies",
  family_history:    "Family history",
  addictions:        "Addictions",
  anthropometric:    "Anthropometric data",
  lab_investigations: "Lab investigations",
  food_preferences:  "Food preferences",
  daily_schedule:    "Daily schedule",
  activity:          "Physical activity",
  drug_schedule:     "Drug schedule",
};

// Format a Date/ISO into "26 Jun 2026, 12:45 PM" (matches spec mock).
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const formatStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dd = d.getDate();
  const mm = MONTHS[d.getMonth()];
  const yy = d.getFullYear();
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${dd} ${mm} ${yy}, ${h}:${m} ${ampm}`;
};

const ReviewSummary = ({ items, startedAt, onClose, onStartReview, onFinishEmpty }) => {
  // Reviewable = anything not discarded. `proposed`, `added_in_call` and
  // `edited_in_call` all get one line in the review's per-section pass.
  const bySection = items.reduce((acc, it) => {
    if (!it.targetSection) return acc;
    if (it.state === "discarded_in_call" || it.state === "discarded_in_review") return acc;
    acc[it.targetSection] = (acc[it.targetSection] || 0) + 1;
    return acc;
  }, {});
  const sections = Object.keys(bySection);
  const total = sections.reduce((n, s) => n + bySection[s], 0);

  return (
    <div className="ait-review-summary" role="dialog" aria-modal="true" aria-label="Review captured items">
      <header className="ait-summ-head">
        <span className="ait-summ-icon-tile" aria-hidden="true">
          <Sparkle className="ait-summ-icon-spark" />
        </span>
        <div className="ait-summ-titles">
          <h2 className="ait-summ-title">Review captured items</h2>
          <p className="ait-summ-sub">{formatStamp(startedAt)}</p>
        </div>
        <button type="button" className="ait-summ-close" aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </header>

      <div className="ait-summ-body">
        <p className="ait-summ-eyebrow">Captured this call</p>
        <div className="ait-summ-list">
          {sections.length === 0 ? (
            <p className="ait-summ-empty">Nothing was captured.</p>
          ) : sections.map((section) => (
            <div className="ait-summ-row" key={section}>
              <span className="ait-summ-row-label">
                {(SECTION_LABEL[section] || section).toUpperCase()} ({bySection[section]})
              </span>
              {/* Row is preview-only (no drill-down here — the next
                  screen surfaces each entry in context), so the "›"
                  chevron was suggesting an interaction that doesn't
                  exist. Removed per 2026-09-17 ask. */}
            </div>
          ))}
        </div>

        <div className="ait-summ-summary">
          <span className="ait-summ-summary-label">Summary</span>
          <span className="ait-summ-summary-total">
            <strong>{total}</strong> mentioned and added
          </span>
        </div>
      </div>

      <footer className="ait-summ-footer">
        {total === 0 ? (
          /* Nothing to review — the reviewer's only action is to mark
             the transcript as reviewed and close the dialog. The
             parent handles the backend flip; see AiTranscriptModal
             for the SaveSection call that flips status → reviewed. */
          <button
            type="button"
            className="ait-summ-cta"
            onClick={onFinishEmpty || onClose}
          >Finish</button>
        ) : (
          <button
            type="button"
            className="ait-summ-cta"
            onClick={onStartReview}
          >Start review</button>
        )}
      </footer>
    </div>
  );
};

ReviewSummary.propTypes = {
  items:          PropTypes.array.isRequired,
  startedAt:      PropTypes.string,
  onClose:        PropTypes.func.isRequired,
  onStartReview:  PropTypes.func.isRequired,
  /** Empty-transcript Finish handler. Flips backend status → reviewed
   *  and closes the dialog. Falls back to `onClose` when omitted. */
  onFinishEmpty:  PropTypes.func,
};
ReviewSummary.defaultProps = { startedAt: "", onFinishEmpty: null };

export { SECTION_LABEL, formatStamp };
export default ReviewSummary;
